// services/notificationService.ts
import { useMutation, useQuery } from "@tanstack/react-query";
import { instance, service } from "../instance";


const apiPrefix = service === 'glo' ? '/admin-dashboard' : '/admin';

// Types
export interface TriggerAction {
  type: "send_sms" | "send_whatsapp" | "add_bonus" | "referral_reward";
  template?: string;
  channel?: "sms" | "whatsapp";
  bonus_type?: "lives" | "credits" | "points";
  count?: number;
}

export interface Trigger {
  id: string;
  name: string;
  trigger_type: string;
  criteria_value: string;
  description: string;
  actions: TriggerAction[];
  is_active: boolean;
  schedule_date: string | null;
  schedule_time: string | null;
  last_run: string | null;
  users_matched: number;
  created_by: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TriggersResponse {
  data: {
    triggers: Trigger[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export interface CreateTriggerPayload {
  name: string;
  trigger_type: string;
  criteria_value: string;
  description: string;
  actions: TriggerAction[];
  is_active: boolean;
  schedule_date: string | null;
  schedule_time: string | null;
}

export interface UpdateTriggerPayload {
  name: string;
  criteria_value: string;
  description: string;
  actions: TriggerAction[];
  is_active: boolean;
  schedule_date: string | null;
  schedule_time: string | null;
}

export interface MessageLog {
  id: string;
  user: string;
  channel: "SMS" | "WHATSAPP";
  message: string;
  status: "delivered" | "read" | "failed" | "sent";
  cost: string;
  timestamp: string;
  trigger: string;
}

export interface MessageLogsResponse {
  success: boolean;
  data: {
    messageLogs: MessageLog[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
  message: string;
}

export interface StatsData {
  totalMessages: {
    value: string;
    change: number;
    comparison: string;
  };
  deliveryRate: {
    value: string;
    change: number;
    comparison: string;
  };
  totalCost: {
    value: string;
    period: string;
  };
  activeTriggers: {
    value: string;
    status: string;
  };
  channelDistribution: {
    sms: {
      count: string;
      percentage: string;
    };
    whatsapp: {
      count: string;
      percentage: string;
    };
  };
}

export interface StatsResponse {
  success: boolean;
  data: StatsData;
  message: string;
}
// Update your notificationService types for event tracking
export interface EventStats {
  totalEvents: number;
  uniqueUsers: number;
  latestEvent: string;
  eventChange: number;
  userChange: number;
  timeRange: string;
  previousPeriod: {
    totalEvents: number;
    uniqueUsers: number;
  };
}

export interface TrackedEventType {
  type: string;
  count: number;
  uniqueUsers: number;
  previousCount: number;
  previousUniqueUsers: number;
  countChange: number;
  usersChange: number;
  trend: 'up' | 'down' | 'stable';
}

export interface LiveEvent {
  eventType: string;
  userId: string;
  timestamp: string;
  metadata: string;
}

export interface EventsResponse {
  success: boolean;
  data: {
    eventStats: EventStats;
    trackedEventTypes: TrackedEventType[];
    liveEvents: LiveEvent[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
    filters: {
      search: string;
      eventType: string;
      timeRange: string;
      userId: string;
      timeRangeLabel: string;
    };
    timeRangeInfo: {
      start: string;
      end: string;
      previousStart: string;
      previousEnd: string;
      label: string;
    };
  };
  message: string;
}

// API Calls
export const notificationAPI = {
  // Triggers
  getTriggers: async (params: {
    page?: number;
    limit?: number;
    is_active?: boolean;
    trigger_type?: string;
    search?: string;
  }): Promise<TriggersResponse> => {
    const { data } = await instance.get(`${apiPrefix}/triggers`, {
      params,
    });
    return data;
  },

  createTrigger: async (
    payload: CreateTriggerPayload
  ): Promise<{ trigger: Trigger }> => {
    const { data } = await instance.post(`${apiPrefix}/triggers`, payload);
    return data;
  },

  updateTrigger: async (
    id: string,
    payload: UpdateTriggerPayload
  ): Promise<{ trigger: Trigger }> => {
    const { data } = await instance.put(
      `${apiPrefix}/triggers/${id}`,
      payload
    );
    return data;
  },

  deleteTrigger: async (id: string): Promise<{ message: string }> => {
    const { data } = await instance.delete(`${apiPrefix}/triggers/${id}`);
    return data;
  },

  // Message Logs
  getMessageLogs: async (params: {
    channel?: string;
    status?: string;
    page?: number;
    limit?: number;
    search?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<MessageLogsResponse> => {
    const { data } = await instance.get(`${apiPrefix}/logs`, { params });
    return data;
  },

  // Statistics
  getMessageStats: async (params: {
    period?: string;
  }): Promise<StatsResponse> => {
    const { data } = await instance.get(`${apiPrefix}/messages/stats`, {
      params,
    });
    return data;
  },

  // Event Tracking
  getEventTracking: async (params: {
    timeRange?: string;
    eventType?: string;
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<EventsResponse> => {
    const { data } = await instance.get(`${apiPrefix}/event-tracking`, {
      params,
    });
    return data;
  },
};

// React Query Hooks
export const useGetTriggers = (params: {
  page?: number;
  limit?: number;
  is_active?: boolean;
  trigger_type?: string;
  search?: string;
}) => {
  return useQuery({
    queryKey: ["triggers", params],
    queryFn: () => notificationAPI.getTriggers(params),
    // keepPreviousData: true,
  });
};

export const useCreateTrigger = () => {
  return useMutation({
    mutationFn: notificationAPI.createTrigger,
    mutationKey: ["create-trigger"],
  });
};

export const useUpdateTrigger = () => {
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateTriggerPayload;
    }) => notificationAPI.updateTrigger(id, payload),
    mutationKey: ["update-trigger"],
  });
};

export const useDeleteTrigger = () => {
  return useMutation({
    mutationFn: notificationAPI.deleteTrigger,
    mutationKey: ["delete-trigger"],
  });
};

export const useGetMessageLogs = (params: {
  channel?: string;
  status?: string;
  page?: number;
  limit?: number;
  search?: string;
  startDate?: string;
  endDate?: string;
}) => {
  return useQuery({
    queryKey: ["message-logs", params],
    queryFn: () => notificationAPI.getMessageLogs(params),
    // keepPreviousData: true,
  });
};

export const useGetMessageStats = (params: { period?: string }) => {
  return useQuery({
    queryKey: ["message-stats", params],
    queryFn: () => notificationAPI.getMessageStats(params),
  });
};

// Add to your existing React Query hooks
export const useGetEventTracking = (params: {
  timeRange?: string;
  eventType?: string;
  page?: number;
  limit?: number;
  search?: string;
  interval?:string
}) => {
  return useQuery({
    queryKey: ['event-tracking', params],
    queryFn: () => notificationAPI.getEventTracking(params),
    // keepPreviousData: true,
  })
}
